// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/audioLevelStore.ts
//
// v1.0 — Diffusion des niveaux audio hors de React.
//
// La boucle de metering (`useAudioMetering`) tourne en requestAnimationFrame.
// Tant qu'elle publiait ses niveaux via `setState`, toute la régie se re-rendait
// jusqu'à 60 fois par seconde dès qu'un son passait — LiveStreamManager, le
// deck, la ControlBar, et tout ce qui pend dessous. Les VU-mètres, eux, n'ont
// besoin que d'écrire une largeur et une couleur sur deux nœuds du DOM.
//
// Ce store est un pub/sub volontairement hors du monde React : la boucle y
// publie, les composants de VU s'y abonnent et écrivent le DOM directement.
// Aucun rendu React n'est déclenché par un changement de niveau.
//
// Seule exception : `subscribeSourceSet`, qui notifie quand l'ENSEMBLE des
// sources mesurées change (une source vient d'être connectée ou retirée). Cet
// événement-là est rare et mérite un rendu — il sert à réévaluer ce qui dépend
// de l'état de l'AudioSourceRegistry, lequel n'est pas réactif.
// ============================================================================

/** Niveaux d'une source, en pourcentage 0–100 (cf. dbToPct dans useAudioMetering). */
export interface SourceLevels {
  /** Moyenne des deux canaux — ce qu'affichent les VU-mètres compacts du deck. */
  avg   : number;
  left  : number;
  right : number;
}

export const SILENT_LEVELS: SourceLevels = { avg: 0, left: 0, right: 0 };

type LevelListener     = (levels: SourceLevels) => void;
type SourceSetListener = (sourceIds: string[]) => void;

export class AudioLevelStore {
  private levels       = new Map<string, SourceLevels>();
  private listeners    = new Map<string, Set<LevelListener>>();
  private setListeners = new Set<SourceSetListener>();
  private lastIdsKey   = '';

  private notifySourceSetIfChanged(): void {
    const key = Array.from(this.levels.keys()).sort().join('|');
    if (key === this.lastIdsKey) return;
    this.lastIdsKey = key;
    const ids = Array.from(this.levels.keys());
    this.setListeners.forEach(fn => fn(ids));
  }

  /**
   * Publie l'instantané complet d'une frame : les sources absentes de `next`
   * sont considérées comme n'étant plus mesurées. C'est le mode d'emploi des
   * publieurs à boucle unique (`useAudioMetering`).
   *
   * Appelé jusqu'à 60 fois par seconde : ne rien allouer d'inutile ici.
   */
  publish(next: Map<string, SourceLevels>): void {
    this.levels = next;

    next.forEach((levels, sourceId) => {
      const subs = this.listeners.get(sourceId);
      if (subs) subs.forEach(fn => fn(levels));
    });

    // Une source abonnée mais absente de la frame n'est plus mesurée : ses
    // VU-mètres doivent retomber à zéro plutôt que rester figés sur la
    // dernière valeur connue.
    this.listeners.forEach((subs, sourceId) => {
      if (!next.has(sourceId)) subs.forEach(fn => fn(SILENT_LEVELS));
    });

    this.notifySourceSetIfChanged();
  }

  /**
   * Met à jour une seule source, sans rien dire des autres. C'est le mode
   * d'emploi des publieurs par-source : l'intercom démarre et arrête une boucle
   * rAF indépendante par device qui parle, et signale explicitement l'arrêt via
   * `remove`.
   *
   * Ne pas mélanger `set`/`remove` et `publish` sur une même instance : le
   * premier mode met à jour la table en place, le second la remplace.
   */
  set(sourceId: string, levels: SourceLevels): void {
    this.levels.set(sourceId, levels);
    const subs = this.listeners.get(sourceId);
    if (subs) subs.forEach(fn => fn(levels));
    this.notifySourceSetIfChanged();
  }

  /** Retire une source suivie par `set` et remet ses abonnés à zéro. */
  remove(sourceId: string): void {
    if (!this.levels.delete(sourceId)) return;
    const subs = this.listeners.get(sourceId);
    if (subs) subs.forEach(fn => fn(SILENT_LEVELS));
    this.notifySourceSetIfChanged();
  }

  /** Valeur courante — pour l'affichage initial, avant la première frame. */
  get(sourceId: string): SourceLevels {
    return this.levels.get(sourceId) ?? SILENT_LEVELS;
  }

  /** Liste des sources actuellement mesurées. */
  getSourceIds(): string[] {
    return Array.from(this.levels.keys());
  }

  /** S'abonne aux niveaux d'une source. Renvoie la fonction de désabonnement. */
  subscribe(sourceId: string, fn: LevelListener): () => void {
    let subs = this.listeners.get(sourceId);
    if (!subs) {
      subs = new Set();
      this.listeners.set(sourceId, subs);
    }
    subs.add(fn);
    return () => {
      const current = this.listeners.get(sourceId);
      if (!current) return;
      current.delete(fn);
      if (current.size === 0) this.listeners.delete(sourceId);
    };
  }

  /** S'abonne aux changements de l'ensemble des sources mesurées. */
  subscribeSourceSet(fn: SourceSetListener): () => void {
    this.setListeners.add(fn);
    return () => { this.setListeners.delete(fn); };
  }

  /** Purge complète — démontage de la régie. */
  reset(): void {
    const hadSources = this.lastIdsKey !== '';
    this.levels = new Map();
    this.lastIdsKey = '';
    this.listeners.forEach(subs => subs.forEach(fn => fn(SILENT_LEVELS)));
    // Sans cette notification, un `useMeteredSourceIds` encore monté resterait
    // sur la dernière liste connue jusqu'à une hypothétique prochaine frame.
    if (hadSources) this.setListeners.forEach(fn => fn([]));
  }
}

/**
 * Niveaux des sources du deck, alimentés par la boucle unique de
 * `useAudioMetering` via `publish`. La régie n'instancie ce hook qu'une fois par
 * page (les vues « deck externe » et « multiview externe » sont des onglets
 * séparés, donc des contextes JS distincts).
 */
export const audioLevelStore = new AudioLevelStore();

/**
 * Niveaux des devices qui parlent en intercom, alimentés par `set`/`remove`
 * depuis MultiviewPanelV2 : une boucle rAF indépendante par device, démarrée et
 * arrêtée avec sa prise de parole. Store distinct d'`audioLevelStore` parce que
 * les deux mesurent des choses différentes et n'ont pas le même mode de
 * publication.
 */
export const intercomLevelStore = new AudioLevelStore();
